import { style, globalStyle } from '@vanilla-extract/css'

import { getFontSize } from '~/styles/mixins/font.css'
import { getMediaQuery } from '~/styles/mixins/mediaQuery.css'
import { getTransition } from '~/styles/mixins/transition.css'
import { cssVariables } from '~/styles/variables/cssVariables.css'

/* Common ========== */

/* Reset:Base */

globalStyle('*, *:before, *:after', {
  boxSizing: 'border-box',
})

globalStyle('html', {
  background: cssVariables.color.white.hex,
  color: cssVariables.color.outerSpace.hex,
  height: '100%',
  lineHeight: 1,
  width: '100%',
  scrollPaddingTop: 72,

  '@media': {
    [getMediaQuery('sm')]: {
      scrollPaddingTop: 80,
    },
  },
})

globalStyle('body', {
  background: cssVariables.color.white.hex,
  color: cssVariables.color.outerSpace.hex,
  backgroundAttachment: 'fixed',
  backgroundPosition: 'top center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  border: 0,
  fontFamily: cssVariables.font.family.main,
  fontWeight: cssVariables.font.weight.medium,
  lineHeight: 1.6,
  margin: 0,
  overflowY: 'auto',
  padding: 0,
  textSizeAdjust: '100%',
  width: '100%',
  fontSize: getFontSize(14),
})

globalStyle(
  'article, aside, details, figcaption, include, footer, header, hgroup, main, nav, section, summary, figure',
  {
    display: 'block',
    margin: 0,
    padding: 0,
  }
)

globalStyle('h1, h2, h3, h4, h5, h6, dl, dt, dd, p, blockquote, ul, ol, li', {
  margin: 0,
  padding: 0,
  verticalAlign: 'baseline',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  fontSize: getFontSize(14),
})

globalStyle('ul, ol', {
  listStyle: 'none',
})

globalStyle('figcaption, span', {
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  wordBreak: 'break-word',
})

globalStyle('audio, canvas, progress, video', {
  display: 'inline-block',
  verticalAlign: 'baseline',
})

globalStyle('audio:not([controls])', {
  display: 'none',
  height: 0,
})

globalStyle('[hidden], template', {
  display: 'none',
})

globalStyle('a', {
  color: cssVariables.color.blueRibbon.hex,
  cursor: 'pointer',
  textDecoration: 'none',
  transition: getTransition([
    {
      property: 'color',
    },
    {
      property: 'opacity',
    },
    {
      property: 'background-color',
    },
    {
      property: 'border-color',
    },
  ]),
})

globalStyle('a:link, a:visited', {
  color: cssVariables.color.blueRibbon.hex,
  textDecoration: 'none',
})

globalStyle('a:hover, a:active', {
  '@media': {
    [getMediaQuery('hover')]: {
      color: cssVariables.color.blueRibbon.hex,
      textDecoration: 'underline',
    },
  },
})

globalStyle('abbr[title]', {
  borderBottom: `1px dotted`,
})

globalStyle('i, em, address', {
  fontStyle: 'normal',
})

globalStyle('b, strong, em', {
  fontWeight: cssVariables.font.weight.bold,
})

globalStyle('mark', {
  backgroundColor: cssVariables.color.black.hex,
  color: cssVariables.color.white.hex,
})

globalStyle('small', {
  fontSize: '100%',
})

globalStyle('sup, sub', {
  fontSize: '75%',
  lineHeight: 0,
  position: 'relative',
  verticalAlign: 'baseline',
})

globalStyle('sup', {
  top: '-0.5em',
})

globalStyle('sub', {
  bottom: '-0.25em',
})

globalStyle('img', {
  border: 0,
  maxWidth: '100%',
  verticalAlign: 'top',
})

globalStyle('img', {
  border: 0,
  maxWidth: '100%',
  verticalAlign: 'top',
})

globalStyle('svg:not(:root)', {
  overflow: 'hidden',
})

globalStyle('hr', {
  boxSizing: 'content-box',
  height: 0,
})

globalStyle('pre', {
  overflow: 'hidden',
})

globalStyle('code, kbd, pre, samp', {
  fontFamily: cssVariables.font.family.main,
  fontSize: getFontSize(10),
})

globalStyle('table', {
  borderCollapse: 'collapse',
  borderSpacing: 0,
})

globalStyle('td, th', {
  padding: 0,
})

/* Reset:Form */

globalStyle('form', {
  margin: 0,
  padding: 0,
})

globalStyle('fieldset', {
  border: 'none',
  margin: 0,
  padding: 0,
})

globalStyle('legend', {
  margin: 0,
  padding: 0,
})

globalStyle('optgroup', {
  fontWeight: cssVariables.font.weight.bold,
})

globalStyle('label', {
  cursor: 'pointer',
  display: 'block',
})

globalStyle('button, input, optgroup, select, textarea', {
  boxSizing: 'border-box',
  fontFamily: cssVariables.font.family.main,
  fontWeight: cssVariables.font.weight.medium,
  color: cssVariables.color.outerSpace.hex,
  lineHeight: 1.6,
  margin: 0,
  outline: 0,
  fontSize: getFontSize(16),
})

globalStyle('button::-moz-focus-inner, input::-moz-focus-inner', {
  border: 0,
  padding: 0,
})

globalStyle(
  'input[type="text"], input[type="tel"], input[type="email"], input[type="date"], input[type="month"], input[type="search"], input[type="password"], input[type="reset"], input[type="number"]',
  {
    appearance: 'none',
    lineHeight: 1.6,
    fontSize: getFontSize(16),
  }
)

globalStyle('input:focus', {
  outline: 0,
})

globalStyle('input:disabled', {
  cursor: 'not-allowed',
})

globalStyle('input[type="file"]:disabled', {
  cursor: 'not-allowed',
})

globalStyle('input[readonly]', {
  pointerEvents: 'none',
})

globalStyle('input[readonly]:hover', {
  '@media': {
    [getMediaQuery('hover')]: {
      pointerEvents: 'none',
    },
  },
})

globalStyle('input[type="number"]', {
  appearance: 'textfield',
})

globalStyle('input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button', {
  appearance: 'none',
  height: 'auto',
  margin: 0,
})

globalStyle('input[type="button"], input[type="submit"]', {
  cursor: 'pointer',
})

globalStyle('input[type="button"]:disabled, input[type="submit"]:disabled', {
  cursor: 'not-allowed',
})

globalStyle('button, html input[type="button"], input[type="reset"], input[type="submit"]', {
  appearance: 'button',
  cursor: 'pointer',
})

globalStyle('button[disabled], html input[disabled]', {
  cursor: 'not-allowed',
})

globalStyle('select', {
  appearance: 'none',
  cursor: 'pointer',
  lineHeight: 1.6,
})

globalStyle('select:focus', {
  outline: 0,
})

globalStyle('select:disabled', {
  cursor: 'not-allowed',
})

globalStyle('select[readonly]', {
  pointerEvents: 'none',
})

globalStyle('select[readonly]:hover', {
  '@media': {
    [getMediaQuery('hover')]: {
      pointerEvents: 'none',
    },
  },
})

globalStyle('textarea', {
  appearance: 'none',
  lineHeight: 1.6,
  resize: 'vertical',
  fontSize: getFontSize(16),
})

globalStyle('textarea:focus', {
  outline: 0,
})

globalStyle('textarea:disabled', {
  cursor: 'not-allowed',
})

globalStyle('textarea[readonly]', {
  pointerEvents: 'none',
})

globalStyle('textarea[readonly]:hover', {
  '@media': {
    [getMediaQuery('hover')]: {
      pointerEvents: 'none',
    },
  },
})

globalStyle(':placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('::placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle(':input-placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('::input-placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('input:placeholder, textarea:placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('input::placeholder, textarea::placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('input:input-placeholder, textarea:input-placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('input::input-placeholder, textarea::input-placeholder', {
  color: cssVariables.color.edward.hex,
})

globalStyle('input:focus:placeholder, textarea:focus:placeholder', {
  color: 'transparent',
})

globalStyle('input:focus::placeholder, textarea:focus::placeholder', {
  color: 'transparent',
})

globalStyle('input:focus:input-placeholder, textarea:focus:input-placeholder', {
  color: 'transparent',
})

globalStyle('input:focus::input-placeholder, textarea:focus::input-placeholder', {
  color: 'transparent',
})

/* Utils ========== */

/* clearfix */
globalStyle('.clearfix', {
  display: 'block',
})

globalStyle('.clearfix:before, .clearfix:after', {
  content: '',
  display: 'block',
})

globalStyle('.clearfix:after', {
  clear: 'both',
})

/* obj */
globalStyle('.obj__sm:not(:root)', {
  display: 'none',
})

globalStyle('.obj__md:not(:root)', {
  display: 'none',
})

globalStyle('.obj__lg:not(:root)', {
  display: 'none',
})

globalStyle('.obj__xl:not(:root)', {
  display: 'none',
})

globalStyle('.obj__xxl:not(:root)', {
  display: 'none',
})

globalStyle('.obj__xs:not(:root)', {
  display: 'block',

  '@media': {
    [getMediaQuery('xs')]: {
      display: 'none',
    },
  },
})

globalStyle('.obj__sm:not(:root)', {
  '@media': {
    [getMediaQuery('xs')]: {
      display: 'block',
    },

    [getMediaQuery('sm')]: {
      display: 'none',
    },
  },
})

globalStyle('.obj__md:not(:root)', {
  '@media': {
    [getMediaQuery('sm')]: {
      display: 'block',
    },

    [getMediaQuery('md')]: {
      display: 'none',
    },
  },
})

globalStyle('.obj__lg:not(:root)', {
  '@media': {
    [getMediaQuery('md')]: {
      display: 'block',
    },

    [getMediaQuery('lg')]: {
      display: 'none',
    },
  },
})

globalStyle('.obj__xl:not(:root)', {
  '@media': {
    [getMediaQuery('lg')]: {
      display: 'block',
    },

    [getMediaQuery('xl')]: {
      display: 'none',
    },
  },
})

globalStyle('.obj__xxl:not(:root)', {
  '@media': {
    [getMediaQuery('xl')]: {
      display: 'block',
    },
  },
})

export const layoutRoot = style({})

export const layoutRoot_container = style({
  display: 'flex',
})

export const layoutRootSide__active = style({})

export const layoutRootSide = style({
  overflow: 'hidden',
  flexShrink: 0,
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: cssVariables.zIndex.sideMenu.wrapper,
  height: '100%',
  backgroundColor: cssVariables.color.outerSpace.hex,
  width: 0,
  transition: getTransition([
    {
      property: 'width',
    },
  ]),

  '@media': {
    [getMediaQuery('md')]: {
      selectors: {
        [`&${layoutRootSide__active}`]: {
          width: 230,
        },
      },
    },
  },
})

export const layoutRootMain = style({
  flexGrow: 1,
  minWidth: 1,
  transition: getTransition([
    {
      property: 'padding',
    },
  ]),
})

export const layoutRootMain__side = style({
  '@media': {
    [getMediaQuery('md')]: {
      paddingLeft: 230,
    },
  },
})

export const layoutRootMain_container = style({
  paddingTop: 56,
  transition: getTransition([
    {
      property: 'padding',
    },
  ]),

  '@media': {
    [getMediaQuery('sm')]: {
      paddingTop: 64,
    },
  },
})
